import { useEffect, useRef, useState } from "react";
import {
    Modal, ModalDialog, Stack, Typography, Button, Chip, Box,
    IconButton, Alert, CircularProgress,
} from "@mui/joy";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';

import ModalDraftsmanManage from "./ModalDraftsmanManage";
import AdditionalStopDialog from '../Quality/AdditionalStopDialog';
import AdditionalStopButton from '../Quality/AdditionalStopButton';
import ManualCompleteEventButton from '../Quality/ManualCompleteEventButton';
import { additionalStopPayload, hasAdditional } from '../Quality/additionalActionsUi';
import { APIKanbanMoveEvent, APIGetEventById, APIStopAdditional } from "../../api";
import ModalKanbanTransition from "../../content/Manager/Kanban/ModalKanbanTransition";
import { track } from "../../utils/tracker";

// Технические этапы совпадают с канбаном; одобрение качества — отдельное действие.
export const STAGES = [
    { key: 'pending_assignment', label: 'На согласовании',
      hint: 'Возврат к назначению. Возможность перехода проверяет сервер.' },
    { key: 'measuring_assigned', label: 'Назначен обмер',
      hint: 'Выбор обмерщика и срока. Проведённые выплаты защищены.' },
    { key: 'closed_by_surveyor', label: 'Закрыт замерщиком',
      hint: 'Обмер завершён, событие ожидает чертёжника.' },
    { key: 'in_drawing', label: 'Взят в черчение',
      hint: 'Для ранее закрытой работы откроется окно повторного черчения.' },
    { key: 'quality_check', label: 'Контроль качества',
      hint: 'Отправить сданный чертёж на проверку. Историческую работу сначала переоткройте.' },
    { key: 'ready_for_delivery', label: 'Готово к выдаче',
      hint: 'Появляется после одобрения текущего чертежа отделом качества.', qualityOnly: true },
    { key: 'completed', label: 'Завершён',
      hint: 'Выдать результат после одобрения текущего круга проверки.' },
    { key: 'cancelled', label: 'Отменён',
      hint: 'Отмена с указанием причины. Возможность перехода проверяет сервер.' },
];

const LABEL_BY_KEY = Object.fromEntries(STAGES.map(s => [s.key, s.label]));

export default function ModalStagePicker({
    open, event, employees = [], token, onClose, onChanged,
}) {
    const [loading, setLoading] = useState(false);
    const [additionalConfirm, setAdditionalConfirm] = useState(null);
    const [additionalBusy, setAdditionalBusy] = useState(false);
    const [additionalError, setAdditionalError] = useState('');
    const [recoveryEvent, setRecoveryEvent] = useState(null);
    const extraPayload = useRef({}), confirmFlight = useRef(false);

    const [error, setError]     = useState('');
    // Сервер попросил данные → показываем готовую модалку перехода с доски
    const [recoveryOpen, setRecoveryOpen] = useState(false);
    const [needsRefresh, setNeedsRefresh] = useState(false);
    const generation = useRef(0);
    const [pending, setPending] = useState(null);   // { to, requiredFields }

    // Текущая колонка приходит с сервера (kanban_stage). У KanbanEventSerializer
    // поле было всегда, в EventsSerializer добавлено серверной частью 7.
    const current = event?.kanban_stage || null;
    const historical = Boolean(event?.date_completed || current === 'completed');
    useEffect(() => {
        generation.current += 1;
        setLoading(false); setError(''); setPending(null);
        setRecoveryOpen(false); setNeedsRefresh(false);
        setAdditionalConfirm(null); setAdditionalError(''); setAdditionalBusy(false); setRecoveryEvent(null); extraPayload.current = {};
        return () => { generation.current += 1; };
    }, [open, event?.id]);

    const refreshAfterRecovery = async () => {
        const run = generation.current;
        setRecoveryOpen(false); setLoading(true); setError(''); setNeedsRefresh(true);
        try {
            const updated = await APIGetEventById(event.id, token);
            if (run !== generation.current) return;
            if (!updated?.id) throw new Error('refresh_failed');
            setNeedsRefresh(false);
            onChanged?.(updated);
            onClose?.();
        } catch (_) {
            if (run === generation.current) setError('Операция выполнена, но карточка не обновилась. Нажмите «Обновить карточку» — повторно создавать работу не нужно.');
        } finally {
            if (run === generation.current) setLoading(false);
        }
    };


    const move = async (to, payload = {}) => {
        if (loading || needsRefresh) return { ok: false, message: 'Дождитесь обновления карточки' };
        if (to === 'ready_for_delivery') {
            const message = 'Стадия «Готово к выдаче» устанавливается после принятия текущего чертежа в разделе «Контроль качества». Через смену стадии проверка не подтверждается.';
            setError(message);
            return { ok: false, message };
        }
        if (to === 'in_drawing' && historical && hasAdditional(event)) {
            setAdditionalError('');
            setAdditionalConfirm({ to, payload, cycle: event.additional_measurement, recovery: true });
            return { ok: false, message: 'Подтвердите снятие дозамера перед повторным черчением' };
        }
        if (to === 'in_drawing' && historical) {
            setError(''); setPending(null); setRecoveryOpen(true);
            return { ok: false, message: 'Открыто повторное черчение' };
        }
        const run = generation.current;
        setError('');
        setLoading(true);
        let res;
        try {
            res = await APIKanbanMoveEvent(event.id, { ...extraPayload.current, ...payload, target_stage: to, ...(to === 'completed' ? { expected_qa_round: event.qa_round } : {}) }, token);
        } catch (e) {
            res = { status: 'error', message: 'Сетевая ошибка' };
        }
        if (run !== generation.current) return { ok: false, message: 'Окно уже изменилось' };
        setLoading(false);

        if (res?.status === 'ok') {
            try {
                track('frontend.event.state_change', {
                    title: `Сменил стадию события #${event.id} → ${to}`,
                    level: 'warning',
                    meta: { event_id: event.id, new_stage: to, old_stage: current },
                });
            } catch (e) {}
            setPending(null);
            onChanged?.(res.event);
            onClose?.();
            return { ok: true };
        }

        if (res?.code === 'additional_confirmation_required') {
            extraPayload.current = {};
            setAdditionalError('');
            setAdditionalConfirm({ to, payload, cycle: res.additional_measurement });
            return { ok: false, message: res.message };
        }
        if (res?.code === 'data_required') {
            setPending({ to, requiredFields: res.required_fields || [] });
            return { ok: false, message: res?.message };
        }

        if (res?.code === 'financial_history' && to === 'in_drawing') {
            extraPayload.current = {};
            setPending(null); setRecoveryOpen(true);
            return { ok: false, message: res.message };
        }
        // Отказ не снимает финансовые ограничения; рядом есть прямое действие.
        extraPayload.current = {};
        const message = res?.message || res?.detail || 'Не удалось сменить стадию';
        setError(message);
        return { ok: false, message };
    };

    const confirmAdditional = async reason => {
        if (confirmFlight.current || !additionalConfirm) return;
        const run = generation.current;
        confirmFlight.current = true; setAdditionalBusy(true); setAdditionalError('');
        const intent = additionalConfirm;
        const extra = additionalStopPayload(intent.cycle, reason);
        try {
            if (intent.recovery) {
                const res = await APIStopAdditional(event.id, extra.expected_additional_id, reason, token);
                if (run !== generation.current) return;
                if (res?.status !== 'ok') { setAdditionalError(res?.message || 'Не удалось снять дозамер'); return; }
                setRecoveryEvent(res.event); onChanged?.(res.event);
                setAdditionalConfirm(null); setRecoveryOpen(true);
            } else {
                extraPayload.current = extra;
                const res = await move(intent.to, { ...intent.payload, ...extra });
                if (run !== generation.current) return;
                setAdditionalConfirm(null);
            }
        } catch (_) { if (run === generation.current) setAdditionalError('Ошибка связи. Обновите карточку перед повтором.'); }
        finally { confirmFlight.current = false; if (run === generation.current) setAdditionalBusy(false); }
    };

    // Подтверждение из ModalKanbanTransition: тот же вызов, но с payload
    const handleTransitionConfirm = (payload) => move(pending.to, payload);

    if (!open) return null;
    if (additionalConfirm) return <AdditionalStopDialog open busy={additionalBusy} error={additionalError}
        target={additionalConfirm.to} onCancel={() => { setAdditionalConfirm(null); extraPayload.current = {}; }} onConfirm={confirmAdditional} />;
    if (recoveryOpen) {
        return <ModalDraftsmanManage open event={recoveryEvent || event} employees={employees} token={token}
            onClose={() => setRecoveryOpen(false)} onDone={refreshAfterRecovery} />;
    }
    if (pending) {
        return (
            <ModalKanbanTransition
                open
                event={event}
                fromStage={current}
                toStage={pending.to}
                requiredFields={pending.requiredFields}
                employees={employees}
                onConfirm={handleTransitionConfirm}
                onCancel={() => { setPending(null); extraPayload.current = {}; }}
            />
        );
    }

    return (
        <Modal open={!!open} onClose={() => { if (!loading) onClose?.(); }} sx={{ zIndex: 10103 }}>
            <ModalDialog sx={{ width: 'min(560px, calc(100vw - 24px))', maxHeight: '88vh', overflowY: 'auto', borderRadius: '14px' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography level="title-md" startDecorator={<AltRouteRoundedIcon />} sx={{ fontWeight: 800 }}>
                        Смена стадии события
                    </Typography>
                    <IconButton size="sm" onClick={() => { if (!loading) onClose?.(); }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>

                <Stack spacing={1.5}>
                    <Box>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>
                            Сейчас
                        </Typography>
                        <Chip variant="soft" color="primary" sx={{ borderRadius: '8px' }}>
                            {LABEL_BY_KEY[current] || '—'}
                        </Chip>
                        {event.manual_closed_at && <Typography level="body-xs" sx={{ mt: 0.75, color: 'text.secondary' }}>
                            Завершено менеджером вручную · {new Date(event.manual_closed_at).toLocaleString('ru-RU')}
                        </Typography>}
                    </Box>

                    <Button variant="soft" color="primary" disabled={loading || needsRefresh}
                        onClick={() => { if (hasAdditional(event)) move('in_drawing'); else { setError(''); setRecoveryOpen(true); } }}>
                        {historical ? 'Повторное черчение / сменить чертёжника' : 'Управление чертёжником'}
                    </Button>
                    <AdditionalStopButton event={event} token={token} disabled={loading || needsRefresh}
                        onChanged={updated => { onChanged?.(updated); onClose?.(); }} />
                    <ManualCompleteEventButton event={event} token={token} disabled={loading || needsRefresh}
                        onChanged={updated => { onChanged?.(updated); onClose?.(); }} />
                    {needsRefresh && <Button loading={loading} onClick={refreshAfterRecovery}>
                        Обновить карточку
                    </Button>}
                    {!!error && (
                        <Alert color="danger" variant="soft" sx={{ borderRadius: '10px' }}>
                            {error}
                        </Alert>
                    )}

                    <Box>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.75 }}>
                            Перевести в
                        </Typography>
                        <Stack spacing={0.75}>
                            {STAGES.filter(s => s.key !== current).map(s => (
                                <Button
                                    key={s.key}
                                    variant="soft"
                                    color="neutral"
                                    disabled={loading || needsRefresh}
                                    onClick={() => move(s.key)}
                                    sx={{
                                        borderRadius: '10px', justifyContent: 'flex-start',
                                        textAlign: 'left', py: 1, flexDirection: 'column',
                                        alignItems: 'flex-start', gap: 0.25,
                                    }}
                                >
                                    <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                                        {s.label}
                                    </Typography>
                                    {/* Последствия перехода — чтобы менеджер не узнавал
                                        о них по факту удалённого чертежа */}
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 400 }}>
                                        {s.hint}
                                    </Typography>
                                </Button>
                            ))}
                        </Stack>
                    </Box>

                    {loading && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size="sm" />
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                Переводим…
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </ModalDialog>
        </Modal>
    );
}
